import React, {
  useState,
  useCallback,
  useContext,
  createContext,
  useEffect,
  useRef, // Use useRef for the one-time initial fetch check
} from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  where,
  getDocs,
  startAfter, // setDoc, // Removed as it was only used for logging // doc, // Removed as it was only used for logging
} from "firebase/firestore";
import { db } from "../../firebase/config"; // Ensure this path is correct
import { useSelector } from "react-redux";

// --- Create Context ---
const ProductContext = createContext();

// --- Constants ---
const PRODUCTS_COLLECTION = "products";
const ITEMS_PER_PAGE = 10; // For main product list and load more
const RECOMMENDED_FETCH_LIMIT = 5;
const ORDER_BY_FIELD = "createdAt"; // Default order by
const ORDER_BY_DIRECTION = "desc";
// const LOGS_COLLECTION = "logs"; // Removed

// --- Provider Component ---
export const CustomerViewData = ({ children }) => {
  // State for the main product list (filtered/searched/paginated)
  const [productsToDisplay, setProductsToDisplay] = useState([]);
  const [lastVisibleProduct, setLastVisibleProduct] = useState(null);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false); // State for the STATIC initial product sections

  const [firstFiveProducts, setFirstFiveProducts] = useState([]);
  const [secondFiveProducts, setSecondFiveProducts] = useState([]);
  const [recommendedLaptops, setRecommendedLaptops] = useState([]);
  const [recommendedDesktops, setRecommendedDesktops] = useState([]); // State for Search functionality

  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]); // Search results might temporarily override productsToDisplay view // State for Redux Filters

  const reduxSelectedBrands = useSelector(
    (state) => state.orebiReducer.checkedBrands
  );
  const reduxSelectedCategory = useSelector(
    (state) => state.orebiReducer.selectedCategory
  ); // --- General Error State ---

  const [error, setError] = useState(null); // --- Flag to ensure initial data fetch runs only once ---

  const hasFetchedInitialData = useRef(false);

  console.log("Redux Brands:", reduxSelectedBrands);
  console.log("Redux Category:", reduxSelectedCategory); // --- Generalized Firestore Fetch Function ---

  const fetchProductsFromFirestore = useCallback(
    async ({
      pageSize,
      filters = [], // Array of Firestore 'where' clauses
      orderByField = ORDER_BY_FIELD,
      orderByDirection = ORDER_BY_DIRECTION,
      startAfterDoc = null, // Used for pagination
      includeStatusAvailable = true, // Option to skip status check for certain queries if needed (default true)
    }) => {
      console.log(
        `🔥 Firestore SDK Fetch: ${PRODUCTS_COLLECTION}, Size: ${pageSize}, Filters: ${
          filters.length
        }, Order: ${orderByField} ${orderByDirection}, StartAfter: ${!!startAfterDoc}`
      );

      const collectionRef = collection(db, PRODUCTS_COLLECTION);
      let queryConstraints = [...filters];

      if (includeStatusAvailable) {
        queryConstraints.unshift(where("status", "==", "Available"));
      }

      queryConstraints.push(orderBy(orderByField, orderByDirection));

      if (startAfterDoc) {
        queryConstraints.push(startAfter(startAfterDoc));
      }
      queryConstraints.push(limit(pageSize));

      const q = query(collectionRef, ...queryConstraints);

      try {
        const snapshot = await getDocs(q);
        const newDocs = snapshot.docs.map((d) => {
          const data = d.data(); // Handle Firestore Timestamps if necessary (as in original code)
          const processedData = { ...data };
          if (data.createdAt?.toDate) {
            processedData.createdAt = data.createdAt.toDate();
          }
          if (data[orderByField]?.toDate) {
            processedData[orderByField] = data[orderByField].toDate();
          }

          return {
            id: d.id,
            ...processedData,
          };
        });
        const lastVisible = snapshot.docs[snapshot.docs.length - 1];
        console.log(
          `✅ Firestore SDK Fetch Success: ${PRODUCTS_COLLECTION}, Fetched: ${
            newDocs.length
          }, HasMore: ${newDocs.length === pageSize}`
        );
        return { docs: newDocs, lastVisibleDoc: lastVisible };
      } catch (err) {
        console.error(
          `❌ Firestore SDK Fetch Error (${PRODUCTS_COLLECTION}):`,
          err.message
        ); // setError(`Failed to fetch products: ${err.message}`); // Consider separate error states if needed
        throw err; // Re-throw to be caught by calling functions
      }
    },
    [] // Dependencies for useCallback
  ); // --- FETCH AND SET MAIN PRODUCTS (Handles initial load IF filtered/searched, filters, search, load more) --- // This function now ONLY manages productsToDisplay, not firstFive/secondFive

  const fetchAndSetProducts = useCallback(
    async ({ reset = false, searchTriggered = false }) => {
      if (isLoadingProducts && !reset) return; // Prevent multiple simultaneous loads unless it's a reset

      console.log(
        `🚀 Fetching Main Products List: Reset=${reset}, SearchTriggered=${searchTriggered}, Brands=${reduxSelectedBrands.join(
          ","
        )}, Category=${reduxSelectedCategory}`
      );
      setIsLoadingProducts(true); // setError(null); // Consider if this should clear error from initial fetches
      if (reset) {
        setProductsToDisplay([]);
        setLastVisibleProduct(null);
        setHasMoreProducts(true); // Assume more products on reset
      }

      const firestoreFilters = []; // Apply filters and search terms

      if (reduxSelectedBrands && reduxSelectedBrands.length > 0) {
        firestoreFilters.push(where("brand", "in", reduxSelectedBrands));
      }

      if (reduxSelectedCategory) {
        firestoreFilters.push(where("category", "==", reduxSelectedCategory));
      }

      const trimmedSearchTerm = searchTerm.trim();
      if (searchTriggered && trimmedSearchTerm) {
        // Basic text search requires range queries on the field
        firestoreFilters.push(where("productName", ">=", trimmedSearchTerm));
        firestoreFilters.push(
          where("productName", "<=", trimmedSearchTerm + "\uf8ff")
        );
      }

      try {
        // Note: includeStatusAvailable defaults to true, so no need to add explicitly here
        const { docs: newProducts, lastVisibleDoc } =
          await fetchProductsFromFirestore({
            pageSize: ITEMS_PER_PAGE,
            filters: firestoreFilters,
            orderByField: ORDER_BY_FIELD, // Or dynamic based on context
            orderByDirection: ORDER_BY_DIRECTION,
            startAfterDoc: reset ? null : lastVisibleProduct,
          });

        setProductsToDisplay((prevProducts) =>
          reset ? newProducts : [...prevProducts, ...newProducts]
        );
        setLastVisibleProduct(lastVisibleDoc);
        setHasMoreProducts(newProducts.length === ITEMS_PER_PAGE);

        if (searchTriggered) {
          setSearchResults(newProducts); // Update searchResults specifically if a search was done
        }

        console.log(
          `✅ Main Products List Fetch Success: Fetched ${
            newProducts.length
          }, HasMore: ${newProducts.length === ITEMS_PER_PAGE}`
        );
      } catch (error) {
        setError(`Failed to fetch main products: ${error.message}`);
        setHasMoreProducts(false); // Stop pagination on error
      } finally {
        setIsLoadingProducts(false);
        if (searchTriggered) setIsSearching(false); // Reset search loading state
      }
    },
    [
      fetchProductsFromFirestore,
      lastVisibleProduct,
      reduxSelectedBrands,
      reduxSelectedCategory,
      searchTerm, // searchTerm is a dependency because handleSearch uses it to trigger fetch
      isLoadingProducts, // Dependency to check before fetching
    ]
  ); // --- Handle "Load More" for the main product list ---

  const handleLoadMore = useCallback(() => {
    if (!isLoadingProducts && hasMoreProducts) {
      console.log("Load More clicked, fetching next page for main list..."); // Pass searchTriggered flag if there's an active search term
      fetchAndSetProducts({
        reset: false,
        searchTriggered: !!searchTerm.trim(),
      });
    }
  }, [isLoadingProducts, hasMoreProducts, fetchAndSetProducts, searchTerm]); // searchTerm added as dependency // --- Handle Product Name Search ---

  const handleSearch = useCallback(() => {
    const trimmedSearchTerm = searchTerm.trim();
    setIsSearching(true);
    setSearchResults([]); // Clear previous search results immediately // Always reset the main product list state when a search is initiated or cleared
    fetchAndSetProducts({ reset: true, searchTriggered: !!trimmedSearchTerm });
  }, [searchTerm, fetchAndSetProducts]); // searchTerm and fetchAndSetProducts are dependencies // --- FETCH RECOMMENDED LAPTOPS (Static, fetch once) ---

  const fetchRecommendedLaptopsData = useCallback(async () => {
    console.log("🚀 Fetching Recommended Laptops (static)..."); // Add separate loading/error states if needed for these sections
    try {
      const { docs: laptops } = await fetchProductsFromFirestore({
        pageSize: RECOMMENDED_FETCH_LIMIT,
        filters: [where("recommended", "==", "Laptops")], // status == "Available" is included by default in fetchProductsFromFirestore
      });
      setRecommendedLaptops(laptops);
      console.log("✅ Recommended Laptops fetched.");
    } catch (err) {
      console.error("Error fetching recommended laptops:", err); // setErrorRecommendedLaptops(err.message); // Handle this error appropriately
    }
  }, [fetchProductsFromFirestore]); // fetchProductsFromFirestore is a dependency // --- FETCH RECOMMENDED DESKTOPS (Static, fetch once) ---

  const fetchRecommendedDesktopsData = useCallback(async () => {
    console.log("🚀 Fetching Recommended Desktops (static)..."); // Add separate loading/error states if needed for these sections
    try {
      const { docs: desktops } = await fetchProductsFromFirestore({
        pageSize: RECOMMENDED_FETCH_LIMIT,
        filters: [where("recommended", "==", "Desktops")], // status == "Available" is included by default in fetchProductsFromFirestore
      });
      setRecommendedDesktops(desktops);
      console.log("✅ Recommended Desktops fetched.");
    } catch (err) {
      console.error("Error fetching recommended desktops:", err); // setErrorRecommendedDesktops(err.message); // Handle this error appropriately
    }
  }, [fetchProductsFromFirestore]); // fetchProductsFromFirestore is a dependency // --- EFFECT: Initial Data Fetch (Runs ONCE on mount) --- // Removed logDatabaseRead function // --- FETCH FIRST FIVE PRODUCTS ---

  const fetchFirstFiveProductsData = useCallback(async () => {
    console.log("🚀 Fetching Recommended Best Sellers (static)...");

    try {
      const { docs: BestSellers } = await fetchProductsFromFirestore({
        pageSize: RECOMMENDED_FETCH_LIMIT,
        filters: [where("recommended", "==", "Best Sellers")],
      });

      setFirstFiveProducts(BestSellers); // Removed logDatabaseRead call

      console.log("✅ Recommended Best Sellers fetched.");
    } catch (err) {
      console.error("Error fetching recommended Best Sellers:", err);
    }
  }, [fetchProductsFromFirestore]); // --- FETCH SECOND FIVE PRODUCTS ---

  const fetchSecondFiveProductsData = useCallback(async () => {
    console.log("🚀 Fetching Recommended Best Sellers (static)...");

    try {
      const { docs: NewArrivals } = await fetchProductsFromFirestore({
        pageSize: RECOMMENDED_FETCH_LIMIT,
        filters: [where("recommended", "==", "New Arrivals")],
      });

      setSecondFiveProducts(NewArrivals); // Removed logDatabaseRead call

      console.log("✅ Recommended Best Sellers fetched.");
    } catch (err) {
      console.error("Error fetching recommended Best Sellers:", err);
    }
  }, [fetchProductsFromFirestore]);

  useEffect(() => {
    if (hasFetchedInitialData.current) {
      console.log("🤷‍♀️ Initial data already fetched, skipping.");
      return;
    }

    console.log("🚀 Effect: Initial data fetch triggered.");

    fetchRecommendedLaptopsData();
    fetchRecommendedDesktopsData(); // Check if there are any initial filters or search terms from Redux/State
    fetchFirstFiveProductsData(); // Fetch firstFiveProducts data
    fetchSecondFiveProductsData(); // Fetch secondFiveProducts data

    const initialFiltersActive =
      (reduxSelectedBrands && reduxSelectedBrands.length > 0) ||
      !!reduxSelectedCategory ||
      !!searchTerm.trim();

    if (!initialFiltersActive) {
      // Only fetch initial 10 for firstFive/secondFive if NO initial filters/search
      console.log(
        "🚀 Effect: Fetching initial 10 products for firstFive/secondFive and main list."
      );
      setIsLoadingProducts(true);
      setError(null);
      const fetchInitialTen = async () => {
        try {
          // Fetch the first 10 available products without any extra filters
          const { docs: initialProducts, lastVisibleDoc } =
            await fetchProductsFromFirestore({
              pageSize: ITEMS_PER_PAGE,
              filters: [], // No brand/category/search filters initially
              orderByField: ORDER_BY_FIELD,
              orderByDirection: ORDER_BY_DIRECTION,
              startAfterDoc: null,
            });

          setProductsToDisplay(initialProducts); // Set the main list
          setLastVisibleProduct(lastVisibleDoc);
          setHasMoreProducts(initialProducts.length === ITEMS_PER_PAGE); // Populate firstFive and secondFive from this initial fetch
          setSecondFiveProducts(initialProducts.slice(5, ITEMS_PER_PAGE));

          console.log(
            "✅ Initial 10 products fetched and split for firstFive/secondFive and main list."
          );
        } catch (err) {
          console.error("❌ Error fetching initial 10 products:", err);
          setError(`Failed to fetch initial products: ${err.message}`);
          setHasMoreProducts(false);
        } finally {
          setIsLoadingProducts(false);
        }
      };

      fetchInitialTen();
    } else {
      console.log(
        "🚀 Effect: Initial filters/search active, fetching main product list with filters."
      );
      fetchAndSetProducts({
        reset: true,
        searchTriggered: !!searchTerm.trim(),
      });
    } // Mark initial fetch as complete

    hasFetchedInitialData.current = true;
  }, [
    fetchProductsFromFirestore,
    fetchRecommendedLaptopsData,
    fetchRecommendedDesktopsData,
    fetchFirstFiveProductsData,
    fetchSecondFiveProductsData,
    reduxSelectedBrands,
    reduxSelectedCategory,
    searchTerm,
  ]); // --- EFFECT: Trigger Main Product List Fetch when Filters Change (Runs AFTER initial mount) --- // This effect handles subsequent changes to Redux filters *after* the initial load // Add dependencies used inside the effect for thoroughness, though hasFetchedInitialData prevents re-running after first mount
  useEffect(() => {
    if (hasFetchedInitialData.current) {
      console.log(
        "🚀 Effect: Redux filters changed, resetting and fetching main product list."
      ); // Reset the main product list and fetch based on current filters/search // firstFive/secondFive/recommended lists are NOT affected by this call
      fetchAndSetProducts({
        reset: true,
        searchTriggered: !!searchTerm.trim(),
      });
    }
  }, [reduxSelectedBrands, reduxSelectedCategory]); // Depends on Redux filters // Context Value

  const value = {
    // Main product list (dynamic based on filters/search/pagination)
    productsToDisplay,
    isLoadingProducts,
    hasMoreProducts,
    handleLoadMore, // Search

    searchTerm,
    setSearchTerm,
    handleSearch,
    isSearching,
    searchResults, // Might be needed if search results are shown separately // STATIC initial product sections

    firstFiveProducts,
    secondFiveProducts,
    recommendedLaptops,
    recommendedDesktops, // Error

    error,
  };

  return (
    <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
  );
};

// --- Custom Hook to use the Context ---
export const useProducts = () => {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error(
      "useProducts must be used within a CustomerViewData provider"
    );
  }
  return context;
};
